import CheckIcon from '@mui/icons-material/Check';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  type CommentReportReason,
  CommentReportReasonSchema,
  type ReportStatus,
  ReportStatusSchema,
} from '@q4udoc/types';
import { useState } from 'react';
import {
  Button,
  Confirm,
  DatagridConfigurable,
  DateField,
  FunctionField,
  List,
  SelectField,
  SelectInput,
  Show,
  SimpleShowLayout,
  TextField,
  TextInput,
  TopToolbar,
  useRecordContext,
} from 'react-admin';
import { ReasonChip, StatusChip } from '../components/ChoiceChips';
import { ListActions } from '../components/ListActions';
import { commonListSx } from '../components/listStyles';
import { ScrollableDatagrid } from '../components/ScrollableDatagrid';
import { TruncatedTextField } from '../components/TruncatedTextField';
import { useResourceAction } from '../hooks/useResourceAction';

// Reported comments: a user reports a comment → it is hidden pending review →
// an admin either Restores it (un-hides) or Removes it (soft-delete tombstone).
// The report carries a snapshot of the comment text + author, so it stays
// meaningful even after the comment is removed.

// Ids come from the shared enums so a new status or reason cannot be missed here; the human
// labels stay local and are the single source for the list chips and the Show page alike.
const STATUS_LABELS: Record<ReportStatus, string> = {
  pending: 'Pending',
  dismissed: 'Dismissed (restored)',
  actioned: 'Actioned (removed)',
};
const STATUS_CHOICES = ReportStatusSchema.options.map((id) => ({ id, name: STATUS_LABELS[id] }));

const REASON_LABELS: Record<CommentReportReason, string> = {
  misinformation: 'Medical Misinformation',
  harassment: 'Harassment',
  spam: 'Spam',
  inappropriate: 'Inappropriate',
  off_topic: 'Off Topic',
  other: 'Other',
};
const REASON_CHOICES = CommentReportReasonSchema.options.map((id) => ({
  id,
  name: REASON_LABELS[id],
}));

const commentReportFilters = [
  <SelectInput key="status" source="status" label="Status" choices={STATUS_CHOICES} alwaysOn />,
  <TextInput key="reportedUserId" source="reportedUserId" label="Reported User ID" />,
];

export const CommentReportList = () => (
  <List
    filters={commentReportFilters}
    actions={<ListActions />}
    sx={commonListSx}
    sort={{ field: 'createdAt', order: 'DESC' }}
  >
    <ScrollableDatagrid>
      <DatagridConfigurable rowClick="show" bulkActionButtons={false}>
        <FunctionField
          label="Status"
          render={() => (
            <StatusChip
              choices={STATUS_CHOICES}
              colors={{ pending: 'warning', dismissed: 'default', actioned: 'error' }}
            />
          )}
        />
        <FunctionField label="Reason" render={() => <ReasonChip choices={REASON_CHOICES} />} />
        <FunctionField
          label="Comment"
          render={() => <TruncatedTextField source="commentText" maxLength={60} />}
        />
        <TextField source="reportedUsername" label="Author" />
        <TextField source="reporterDisplayName" label="Reporter" />
        <DateField source="createdAt" label="Reported" showTime />
      </DatagridConfigurable>
    </ScrollableDatagrid>
  </List>
);

// --- Show-page actions -------------------------------------------------------

const RestoreButton = () => {
  const record = useRecordContext();
  const restore = useResourceAction('comment-reports', 'dismiss', {
    success: 'Comment restored',
    error: 'Error restoring comment',
  });
  if (record?.status !== 'pending') return null;
  return (
    <Button label="Restore" onClick={() => restore()} startIcon={<CheckIcon />} color="success" />
  );
};

const RemoveButton = () => {
  const record = useRecordContext();
  const [open, setOpen] = useState(false);
  const remove = useResourceAction('comment-reports', 'remove', {
    success: 'Comment removed',
    error: 'Error removing comment',
  });
  if (record?.status !== 'pending') return null;

  return (
    <>
      <Button
        label="Remove"
        onClick={() => setOpen(true)}
        startIcon={<DeleteIcon />}
        color="error"
      />
      <Confirm
        isOpen={open}
        fullWidth
        title="Remove this comment?"
        content="The comment is replaced with a “[deleted]” tombstone and any replies stay in place."
        confirm="Remove"
        confirmColor="warning"
        onConfirm={async () => {
          if (await remove()) setOpen(false);
        }}
        onClose={() => setOpen(false)}
      />
    </>
  );
};

const CommentReportShowActions = () => (
  <TopToolbar>
    <RestoreButton />
    <RemoveButton />
  </TopToolbar>
);

export const CommentReportShow = () => (
  <Show actions={<CommentReportShowActions />}>
    <SimpleShowLayout>
      <TextField source="commentText" label="Comment" />
      <SelectField source="status" choices={STATUS_CHOICES} />
      <SelectField source="reason" choices={REASON_CHOICES} />
      <TextField source="description" label="Reporter Note" />
      <TextField source="reportedUsername" label="Comment Author" />
      <TextField source="reportedUserId" label="Author User ID" />
      <TextField source="reporterDisplayName" label="Reporter" />
      <TextField source="reporterEmail" label="Reporter Email" />
      <TextField source="questionId" label="Question ID" />
      <DateField source="createdAt" label="Reported At" showTime />
      <DateField source="resolvedAt" label="Resolved At" showTime />
      <TextField source="resolvedBy" label="Resolved By" />
    </SimpleShowLayout>
  </Show>
);
